/**
 * Reversi Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 *
 * Follows SnakesLaddersStatisticsService pattern.
 */

import {
  ReversiStatistics,
  ReversiGameResult,
  ReversiGameStats,
  ReversiDifficulty,
  ReversiGameSettings,
  PlayerColor,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class ReversiStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'reversi_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): ReversiStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): ReversiGameStats {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageScore: 0,
      highestScore: 0,
      totalDiscsFlipped: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): ReversiStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as ReversiStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get reversi statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: ReversiGameResult,
    playerColor: PlayerColor,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerColor);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerColor);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerColor);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record reversi game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: ReversiGameStats,
    result: ReversiGameResult,
    playerColor: PlayerColor
  ): void {
    const totalGames = stats.wins + stats.losses + stats.draws;

    if (result.winner === 'draw') {
      stats.draws++;
    } else if (result.winner === playerColor) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    const newTotalGames = stats.wins + stats.losses + stats.draws;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    // Update average score
    const playerScore = result.score[playerColor];
    stats.averageScore =
      newTotalGames > 0
        ? (stats.averageScore * totalGames + playerScore) / newTotalGames
        : playerScore;

    // Update highest score
    if (playerScore > stats.highestScore) {
      stats.highestScore = playerScore;
    }

    // Update total flips
    stats.totalDiscsFlipped += result.totalFlips;
  }

  /** Get statistics for a specific mode and difficulty. */
  getModeStatistics(
    mode: ReversiGameSettings['mode'],
    difficulty?: ReversiDifficulty,
    userId?: string
  ): ReversiGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset reversi statistics:', error);
      return false;
    }
  }
}
